// Auth context for the admin area. Tracks the signed-in user and whether they
// carry the `admin` custom claim, and exposes sign-in/out + bootstrap helpers.
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/lib/firebase";

const AuthContext = createContext(null);

const bootstrapAdminFn = httpsCallable(functions, "bootstrapAdmin");

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const readClaims = useCallback(async (u, forceRefresh = false) => {
    if (!u) return setIsAdmin(false);
    const token = await u.getIdTokenResult(forceRefresh);
    setIsAdmin(token.claims.admin === true);
  }, []);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      await readClaims(u);
      setLoading(false);
    });
  }, [readClaims]);

  const signIn = useCallback(
    async (email, password) => {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      await readClaims(cred.user, true);
      return cred.user;
    },
    [readClaims]
  );

  const signOutUser = useCallback(async () => {
    await signOut(auth);
    setIsAdmin(false);
  }, []);

  // Try to self-provision admin (allowlisted emails only), then refresh claims.
  const bootstrapAdmin = useCallback(async () => {
    await bootstrapAdminFn();
    if (auth.currentUser) await readClaims(auth.currentUser, true);
  }, [readClaims]);

  /* ---- password reset (Firebase-hosted email + oobCode) ----------------
   * Firebase Auth sends the reset mail itself — no SMTP/Cloud Function is
   * involved. The emailed link carries a single-use `oobCode` that expires in
   * ~1h; /admin/reset exchanges it for a password change. */

  // Never surfaces whether the address is registered — callers show the same
  // confirmation either way (see ForgotPassword).
  const requestPasswordReset = useCallback(
    (email) => sendPasswordResetEmail(auth, email),
    []
  );

  // Validates an oobCode and resolves to the email it belongs to.
  const verifyResetCode = useCallback(
    (oobCode) => verifyPasswordResetCode(auth, oobCode),
    []
  );

  // Consumes the oobCode and sets the new password. Firebase revokes the
  // account's other sessions on success.
  const completePasswordReset = useCallback(
    (oobCode, newPassword) => confirmPasswordReset(auth, oobCode, newPassword),
    []
  );

  const value = {
    user,
    isAdmin,
    loading,
    signIn,
    signOutUser,
    bootstrapAdmin,
    requestPasswordReset,
    verifyResetCode,
    completePasswordReset,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
